/**
 * Rio's draft, as a card the recruiter shapes directly: how long the
 * interview runs, what it covers and in what order, which skills matter most,
 * questions of their own, how Rio sounds, and whether there's a work task.
 * Every control changes the draft at once. The calls to Rio are the check of
 * a custom question as it's typed (useQuestionCheck) and the bar Rio writes
 * for it once it's added (useQuestionBar).
 */
import {
  useId,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type {
  InterviewerConfig,
  InterviewerPreview,
  Posting,
  SectionKey,
  Tone,
  WorkspaceKind,
} from '../../services/interviewer.types';
import { TONES, WORKSPACE_KINDS } from '../../services/interviewer.types';
import {
  MAX_AVOID_TOPICS,
  MAX_FOCUS,
  MAX_QUESTIONS,
  MAX_QUESTION_LENGTH,
  MAX_TOTAL,
  MIN_TOTAL,
  WORKSPACE_LABELS,
  addAvoidTopic,
  addQuestion,
  HELD_LINE,
  cycleSkill,
  holdsQuestions,
  isComingSoon,
  moveSection,
  questionSections,
  removeAvoidTopic,
  removeQuestion,
  setExpectationLevel,
  setTone,
  setTotal,
  setWorkspace,
  skillFocus,
  stepSection,
  toggleSection,
  updateQuestion,
} from '../../interviewer/model';
import SectionTimeline from './SectionTimeline';
import QuestionBar from './QuestionBar';
import WorkTaskKinds from './WorkTaskKinds';
import { useWorkTasks } from './useWorkTasks';
import { useQuestionBar } from './useQuestionBar';
import { useQuestionCheck } from './useQuestionCheck';
import { CloseIcon, LessFocusIcon, MoreFocusIcon, PlusIcon } from './icons';
import styles from './index.module.css';

interface Props {
  config: InterviewerConfig;
  preview: InterviewerPreview;
  /** The job as written so far: its skills are the chips, and Rio checks against it. */
  posting: Posting;
  status: string;
  disabled: boolean;
  highlight: Set<string>;
  onChange: (change: (current: InterviewerConfig) => InterviewerConfig) => void;
  /** Shown above the controls, e.g. "the posting changed since the draft". */
  notice?: ReactNode;
}

const TONE_LABELS: Record<Tone, string> = {
  warm: 'Warm',
  neutral: 'Neutral',
  brisk: 'Brisk',
};

const TONE_SAMPLES: Record<Tone, string> = {
  warm: 'Thanks for walking me through that, it sounds like a tough week. What did you look at first?',
  neutral: 'Thanks. What did you look at first?',
  brisk: 'Got it. What did you check first?',
};

const FOCUS_TEXT = {
  neutral: 'normal focus',
  more: 'more focus',
  less: 'less focus',
};

const cx = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

const InterviewerCard = ({
  config,
  preview,
  posting,
  status,
  disabled,
  highlight,
  onChange,
  notice,
}: Props) => {
  const ids = useId();
  const region = (name: string) =>
    cx(styles.block, highlight.has(name) && styles.changed);
  const { skills } = posting;
  const sectionTitle = (key: SectionKey) =>
    preview.sections.find((s) => s.key === key)?.title ?? key;
  const enabledSections = config.sections.filter((s) => s.enabled);
  // A question goes in one of the team's own sections that's on; the one it's
  // in already stays listed, so the select can show it.
  const askedIn = questionSections(config);
  const sectionOptions = (current?: SectionKey) =>
    current && !askedIn.some((s) => s.key === current)
      ? [...askedIn, { key: current }]
      : askedIn;
  const fill =
    ((config.targetMinutes - MIN_TOTAL) / (MAX_TOTAL - MIN_TOTAL)) * 100;
  // Questions asked during the work task keep it on (Rio refuses them otherwise).
  const taskHeld =
    config.workspace.enabled && holdsQuestions(config, 'workspace');

  // Custom questions: adding and editing in place.
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newSection, setNewSection] = useState<SectionKey>('skills');
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [topic, setTopic] = useState('');
  const [sketching, setSketching] = useState<Set<string>>(new Set());
  // Rio's own areas a question can count toward (never the posting's skills).
  const { areas } = preview;
  const { notes } = useQuestionBar({
    config,
    posting,
    onChange,
    enabled: !disabled,
    onSketching: setSketching,
  });
  const newProblem = useQuestionCheck(
    config,
    posting,
    adding ? newText : '',
    newSection,
    null,
  );
  const editProblem = useQuestionCheck(
    config,
    posting,
    editing ? editText : '',
    'skills',
    editing,
  );

  // Several work tasks, only where Rio says it opens them; otherwise one kind.
  const multi = preview.workspace.multi;
  const work = useWorkTasks({
    config,
    preview,
    posting,
    onChange,
    active: multi,
  });

  const atQuestionLimit = config.customQuestions.length >= MAX_QUESTIONS;
  const defaultSection: SectionKey =
    askedIn.find((s) => s.key === 'skills')?.key ?? askedIn[0]?.key ?? 'skills';

  const submitQuestion = (event: FormEvent) => {
    event.preventDefault();
    if (!newText.trim() || newProblem) return;
    onChange((c) => addQuestion(c, newText, newSection));
    setNewText('');
    setAdding(false);
  };

  const saveEdit = (id: string) => {
    if (editProblem) return;
    if (editText.trim())
      onChange((c) => updateQuestion(c, id, { text: editText }));
    setEditing(null);
  };

  const submitTopic = (event: FormEvent) => {
    event.preventDefault();
    if (!topic.trim()) return;
    onChange((c) => addAvoidTopic(c, topic));
    setTopic('');
  };

  const onToneKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = TONES.indexOf(config.tone);
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const next = TONES[(index + delta + TONES.length) % TONES.length];
    onChange((c) => setTone(c, next));
    const button = event.currentTarget.querySelector<HTMLButtonElement>(
      `[data-tone="${next}"]`,
    );
    button?.focus();
  };

  return (
    <section
      className={cx(styles.panel)}
      aria-labelledby={`${ids}-title`}
      aria-busy={disabled}
    >
      <div className={styles.cardHead}>
        <div>
          <p className={styles.eyebrow}>Rio, your interviewer</p>
          <h2 className={styles.cardTitle} id={`${ids}-title`}>
            Rio&rsquo;s draft
            <span
              className={cx(
                styles.pill,
                status !== 'Drafted from your posting' && styles.pillAccent,
              )}
            >
              {status}
            </span>
          </h2>
        </div>
        <div className={styles.total}>
          <span className={styles.totalValue}>{config.targetMinutes} min</span>
          <span className={styles.totalLabel}>Interview length</span>
        </div>
      </div>

      {notice}

      <div className={disabled ? styles.busy : undefined}>
        <div className={region('length')} data-region="length">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle} id={`${ids}-length`}>
              How long it runs
            </h3>
            <p className={styles.hint}>
              Sections share the time; moving this rescales them.
            </p>
          </div>
          <div className={styles.lengthRow}>
            <input
              type="range"
              className={styles.range}
              min={MIN_TOTAL}
              max={MAX_TOTAL}
              step={1}
              value={config.targetMinutes}
              aria-labelledby={`${ids}-length`}
              aria-valuetext={`${config.targetMinutes} minutes`}
              disabled={disabled}
              style={{ ['--fill' as string]: `${fill}%` }}
              onChange={(e) => {
                const minutes = Number(e.target.value);
                onChange((c) => setTotal(c, minutes));
              }}
            />
            <div className={styles.scale} aria-hidden>
              <span>{MIN_TOTAL} min</span>
              <span>30 min</span>
              <span>{MAX_TOTAL} min</span>
            </div>
            <div className={styles.share} aria-hidden>
              {enabledSections.map((s) => (
                <span
                  key={s.key}
                  className={cx(styles.shareSegment, styles[`tone-${s.key}`])}
                  style={{ flexGrow: s.minutes }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={region('sections')} data-region="sections">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle}>What the interview covers</h3>
            <p className={styles.hint}>
              Drag to reorder, or use the arrow keys on a handle.
            </p>
          </div>
          <SectionTimeline
            config={config}
            preview={preview}
            disabled={disabled}
            highlight={highlight}
            onToggle={(key) => onChange((c) => toggleSection(c, key))}
            onStep={(key, delta) => onChange((c) => stepSection(c, key, delta))}
            onMove={(from, to) => onChange((c) => moveSection(c, from, to))}
          />
        </div>

        <div className={region('skills')} data-region="skills">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle}>What you want in a candidate</h3>
          </div>
          {skills.length ? (
            <>
              <div
                className={styles.chips}
                role="group"
                aria-label="Skill focus"
              >
                {skills.map((skill) => {
                  const focus = skillFocus(config, skill.key);
                  return (
                    <button
                      key={skill.key}
                      type="button"
                      className={cx(
                        styles.chip,
                        focus === 'more' && styles.chipMore,
                        focus === 'less' && styles.chipLess,
                        skill.required && styles.chipRequired,
                        highlight.has(`skill:${skill.key}`) && styles.changed,
                      )}
                      aria-label={`${skill.label}${skill.required ? ', required' : ''}: ${FOCUS_TEXT[focus]}. Select to change.`}
                      disabled={disabled}
                      onClick={() => onChange((c) => cycleSkill(c, skill.key))}
                    >
                      {focus !== 'neutral' && (
                        <span className={styles.chipMark} aria-hidden>
                          {focus === 'more' ? (
                            <MoreFocusIcon />
                          ) : (
                            <LessFocusIcon />
                          )}
                        </span>
                      )}
                      {skill.label}
                    </button>
                  );
                })}
              </div>
              <p className={styles.legend}>
                <span>
                  Select a skill once for more focus, again for less, and a
                  third time to reset.
                </span>
                <span>
                  Required skills are in bold. Up to {MAX_FOCUS} each.
                </span>
              </p>
            </>
          ) : (
            <p className={styles.hint}>
              Add required and preferred skills to the posting, and you can tell
              Rio which matter most.
            </p>
          )}
        </div>

        <div className={region('questions')} data-region="questions">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle}>Your own questions</h3>
            <p className={styles.hint}>
              {config.customQuestions.length} of {MAX_QUESTIONS}
            </p>
          </div>
          <p className={styles.hint}>
            Rio scores the answer against this bar, the same way it scores its
            own questions.
          </p>
          {config.customQuestions.length > 0 && (
            <ul className={styles.questions}>
              {config.customQuestions.map((q) => (
                <li key={q.id} className={styles.question}>
                  {editing === q.id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        saveEdit(q.id);
                      }}
                      style={{ gridColumn: '1 / -1', display: 'grid', gap: 8 }}
                    >
                      <textarea
                        className={styles.textarea}
                        aria-label="Edit question"
                        value={editText}
                        maxLength={MAX_QUESTION_LENGTH}
                        rows={2}
                        autoFocus
                        aria-invalid={editProblem ? true : undefined}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setEditing(null);
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            saveEdit(q.id);
                          }
                        }}
                      />
                      <p className={styles.fieldError} aria-live="polite">
                        {editProblem}
                      </p>
                      <div className={styles.editRow}>
                        <button
                          type="submit"
                          className={styles.textButton}
                          disabled={!!editProblem}
                        >
                          Save question
                        </button>
                        <button
                          type="button"
                          className={cx(
                            styles.textButton,
                            styles.textButtonQuiet,
                          )}
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.questionText}
                        aria-label={`Edit question: ${q.text}`}
                        disabled={disabled}
                        onClick={() => {
                          setEditing(q.id);
                          setEditText(q.text);
                        }}
                      >
                        {q.text}
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Remove question: ${q.text}`}
                        disabled={disabled}
                        onClick={() => onChange((c) => removeQuestion(c, q.id))}
                      >
                        <CloseIcon />
                      </button>
                      <QuestionBar
                        question={q}
                        areas={areas}
                        disabled={disabled}
                        sketching={sketching.has(q.id)}
                        note={notes[q.id] ?? null}
                        onArea={(area) =>
                          onChange((c) =>
                            updateQuestion(c, q.id, {
                              area: area?.key ?? null,
                              areaLabel: area?.label ?? null,
                            }),
                          )
                        }
                        onCounts={(counts) =>
                          onChange((c) => updateQuestion(c, q.id, { counts }))
                        }
                        onLevel={(level, items) =>
                          onChange((c) =>
                            setExpectationLevel(c, q.id, level, items),
                          )
                        }
                      />
                      <div className={styles.questionMeta}>
                        <label
                          className={styles.hint}
                          htmlFor={`${ids}-${q.id}-section`}
                        >
                          Asked during
                        </label>
                        <select
                          id={`${ids}-${q.id}-section`}
                          className={styles.select}
                          value={q.section}
                          disabled={disabled}
                          onChange={(e) =>
                            onChange((c) =>
                              updateQuestion(c, q.id, {
                                section: e.target.value as SectionKey,
                              }),
                            )
                          }
                        >
                          {sectionOptions(q.section).map((s) => (
                            <option key={s.key} value={s.key}>
                              {sectionTitle(s.key)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {adding ? (
            <form onSubmit={submitQuestion} style={{ display: 'grid', gap: 8 }}>
              <textarea
                className={styles.textarea}
                aria-label="New question"
                placeholder="Tell me about the last time you were paged. What did you change afterwards?"
                value={newText}
                maxLength={MAX_QUESTION_LENGTH}
                rows={2}
                autoFocus
                aria-invalid={newProblem ? true : undefined}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setAdding(false);
                  if (e.key === 'Enter' && !e.shiftKey) submitQuestion(e);
                }}
              />
              <p className={styles.fieldError} aria-live="polite">
                {newProblem}
              </p>
              <div className={styles.editRow}>
                <label className={styles.hint} htmlFor={`${ids}-new-section`}>
                  Asked during
                </label>
                <select
                  id={`${ids}-new-section`}
                  className={styles.select}
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value as SectionKey)}
                >
                  {sectionOptions().map((s) => (
                    <option key={s.key} value={s.key}>
                      {sectionTitle(s.key)}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className={styles.textButton}
                  disabled={!newText.trim() || !!newProblem}
                >
                  Add question
                </button>
                <button
                  type="button"
                  className={cx(styles.textButton, styles.textButtonQuiet)}
                  onClick={() => setAdding(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div>
              <button
                type="button"
                className={styles.textButton}
                disabled={disabled || atQuestionLimit}
                onClick={() => {
                  setNewSection(defaultSection);
                  setAdding(true);
                }}
              >
                <PlusIcon /> Add a question
              </button>
              {atQuestionLimit && (
                <p className={styles.hint}>
                  You can add up to {MAX_QUESTIONS} questions.
                </p>
              )}
            </div>
          )}
        </div>

        <div className={region('tone')} data-region="tone">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle} id={`${ids}-tone`}>
              How Rio sounds
            </h3>
          </div>
          <div
            className={styles.segmented}
            role="radiogroup"
            aria-labelledby={`${ids}-tone`}
            onKeyDown={onToneKey}
          >
            <span
              className={styles.segmentThumb}
              aria-hidden
              style={{ translate: `${TONES.indexOf(config.tone) * 100}% 0` }}
            />
            {TONES.map((tone) => (
              <button
                key={tone}
                type="button"
                role="radio"
                data-tone={tone}
                className={styles.segment}
                aria-checked={config.tone === tone}
                tabIndex={config.tone === tone ? 0 : -1}
                disabled={disabled}
                onClick={() => onChange((c) => setTone(c, tone))}
              >
                {TONE_LABELS[tone]}
              </button>
            ))}
          </div>
          <p className={styles.sample}>
            <span className={styles.sampleLabel}>Rio, after an answer</span>
            &ldquo;{TONE_SAMPLES[config.tone]}&rdquo;
          </p>
        </div>

        <div className={region('workspace')} data-region="workspace">
          <div className={styles.switchRow}>
            <div>
              <h3 className={styles.blockTitle} id={`${ids}-ws`}>
                Work task
              </h3>
              <p className={styles.hint}>
                A short task from the role, worked with an AI assistant beside
                them. Rio asks why afterwards.
              </p>
              {taskHeld && <p className={styles.hint}>{HELD_LINE}</p>}
            </div>
            <button
              type="button"
              role="switch"
              className={styles.switch}
              aria-checked={config.workspace.enabled}
              aria-label="Include a work task"
              title={taskHeld ? HELD_LINE : undefined}
              disabled={disabled || taskHeld}
              onClick={() =>
                multi
                  ? work.toggleTask()
                  : onChange((c) =>
                      setWorkspace(c, { enabled: !c.workspace.enabled }),
                    )
              }
            />
          </div>
          {config.workspace.enabled && multi && (
            <WorkTaskKinds
              config={config}
              preview={preview}
              disabled={disabled}
              problem={work.problem}
              fix={work.fix}
              adjusted={work.adjusted}
              onPick={work.pick}
              onMove={work.move}
            />
          )}
          {config.workspace.enabled && !multi && (
            <>
              <div
                className={styles.kinds}
                role="radiogroup"
                aria-label="Kind of work task"
              >
                {WORKSPACE_KINDS.map((kind: WorkspaceKind) => (
                  <button
                    key={kind}
                    type="button"
                    role="radio"
                    aria-checked={config.workspace.kind === kind}
                    className={cx(
                      styles.chip,
                      config.workspace.kind === kind && styles.chipMore,
                    )}
                    disabled={disabled}
                    onClick={() => onChange((c) => setWorkspace(c, { kind }))}
                  >
                    {WORKSPACE_LABELS[kind]}
                    {isComingSoon(kind, preview.workspace.availableKinds) && (
                      <span className={styles.soon}>Soon</span>
                    )}
                  </button>
                ))}
              </div>
              {preview.workspace.note && (
                <p className={styles.hint}>{preview.workspace.note}</p>
              )}
            </>
          )}
        </div>

        <div className={region('avoid')} data-region="avoid">
          <div className={styles.blockHead}>
            <h3 className={styles.blockTitle}>Topics Rio won&rsquo;t raise</h3>
            <p className={styles.hint}>
              {config.avoidTopics.length} of {MAX_AVOID_TOPICS}
            </p>
          </div>
          {config.avoidTopics.length > 0 && (
            <div className={styles.chips}>
              {config.avoidTopics.map((t) => (
                <span key={t} className={cx(styles.chip, styles.chipLess)}>
                  {t}
                  <button
                    type="button"
                    className={styles.iconButton}
                    style={{
                      width: 24,
                      height: 24,
                      border: 'none',
                      background: 'transparent',
                    }}
                    aria-label={`Remove topic: ${t}`}
                    disabled={disabled}
                    onClick={() => onChange((c) => removeAvoidTopic(c, t))}
                  >
                    <CloseIcon />
                  </button>
                </span>
              ))}
            </div>
          )}
          {config.avoidTopics.length < MAX_AVOID_TOPICS && (
            <form onSubmit={submitTopic} className={styles.editRow}>
              <input
                className={styles.input}
                style={{ flex: '1 1 200px', width: 'auto' }}
                aria-label="Add a topic Rio won't raise"
                placeholder="Internal reorganisation"
                value={topic}
                maxLength={80}
                disabled={disabled}
                onChange={(e) => setTopic(e.target.value)}
              />
              <button
                type="submit"
                className={styles.textButton}
                disabled={disabled || !topic.trim()}
              >
                Add topic
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default InterviewerCard;
